// ============================================================
// ROSTER MIGRATION - one-way v1 → v2 session conversion.
//
// v1 modelled a fight as two uncoupled things: target TABS (one Target
// each) and shared ATTACK PROFILES (Slot[] each, with an untyped
// `_charBindings` map remembering which Character produced one). v2 has
// a single roster of Combatants.
//
// The governing constraint is that combat math must not change. So this
// never tries to invert a Target's flat flags (naturalArmour, machineAP,
// trueGrit, …) back into catalog trait ids - that would be lossy. It
// resolves whatever sheet it has, DIFFS the Target against it, and parks
// the remainder in `overrides`. A pristine spawned marine therefore
// migrates with `overrides: {}`, while a GM's hand-tweaks survive as
// explicit overrides.
//
// Pure: no Firestore, no React, no I/O. The sync layer calls this and
// writes the result.
// ============================================================
import type { Character } from '../character/types';
import type { NarrativeEntry, Slot, Tab, Target } from '../../types';
import type { Combatant, Loadout } from './types';
import type { ValidationWarning } from '../importValidation';
import { characterFromStatblock, combatantFromCharacter, emptyLoadout } from './factories';
import { emptyCharacter } from '../character/factories';
import { normalizeCharacter } from '../character/normalize';
import { combatantFromLegacyTab } from './legacyAdapter';
import { applyLoadoutSlots } from './loadoutAdapter';

// The Target-splitting primitives live one layer down so migration and
// legacyAdapter can both use them without importing each other.
export { diffOverrides, sheetFromTarget, splitTarget, stateFromTarget } from './targetSplit';

export const ROSTER_SCHEMA_VERSION = 2;

// ────────────────────────────────────────────────────────────
// Legacy shapes (v1)
// ────────────────────────────────────────────────────────────

interface LegacyTab {
  id: string;
  label?: string;
  target: Target;
  narrativeLog?: NarrativeEntry[];
  sourceCharacter?: Character | null;
}

interface LegacyAtkProfiles {
  current?: string;
  list?: Record<string, Slot[]>;
  _charBindings?: Record<string, { character?: Character } | undefined>;
}

export interface LegacySession {
  tabs?: LegacyTab[];
  activeTab?: string;
  atkProfiles?: LegacyAtkProfiles;
  tgtTemplates?: Record<string, Target>;
}

export interface MigrationResult {
  combatants: Combatant[];
  loadouts: Loadout[];
  /** Statblock templates promoted into the character library. */
  characters: Character[];
  actorId: string | null;
  targetId: string | null;
  warnings: ValidationWarning[];
}

// ────────────────────────────────────────────────────────────
// Slots → loadouts
// ────────────────────────────────────────────────────────────

/**
 * A profile's Slot[] becomes a Loadout of CharacterWeapons.
 *
 * Slot is flat - it merges weapon data with character-derived skillValue
 * and strengthBonus. Those are dropped: under the roster they are
 * projected from the sheet, which is the point.
 */
export function loadoutFromSlots(name: string, slots: Slot[], id?: string): Loadout {
  const loadout = emptyLoadout(name, id);
  loadout.weapons = (slots ?? [])
    .filter((s) => s && !s.isEmpty)
    .map((s, i) => ({
      id: 'wpn-' + (id ?? name).replace(/\s+/g, '-').toLowerCase() + '-' + i,
      name: s.weaponName || s.name || 'Weapon',
      type: s.weaponType,
      damageExpression: s.damageExpression,
      damageType: s.damageType,
      penetration: s.penetration,
      range: s.range,
      rateOfFire: { ...s.rateOfFire },
      specialRules: [...(s.specialRules ?? [])],
      psyRating: s.psyRating ?? 0,
      ammo: { ...s.ammo },
      righteousFury: s.righteousFury,
    }));
  return loadout;
}

/**
 * Synthesise a Character that reproduces an unbound profile's combat
 * numbers. Slots carry skillValue and strengthBonus directly, so the
 * shell's characteristics are set to whatever value resolves back to
 * them: ws/bs from the first melee/ranged slot, str from strengthBonus.
 *
 * This is the lossiest step in the migration, so divergent slots emit a
 * warning rather than being silently flattened.
 */
export function characterFromProfile(name: string, slots: Slot[], warnings: ValidationWarning[] = []): Character {
  const filled = (slots ?? []).filter((s) => s && !s.isEmpty);
  const char = emptyCharacter('char-profile-' + name.replace(/\s+/g, '-').toLowerCase());
  char.name = name;

  const melee = filled.filter((s) => s.weaponType === 'melee');
  const ranged = filled.filter((s) => s.weaponType === 'ranged');
  if (melee.length) char.characteristics.ws.value = melee[0].skillValue;
  if (ranged.length) char.characteristics.bs.value = ranged[0].skillValue;
  if (filled.length) char.characteristics.str.value = filled[0].strengthBonus * 10;

  for (const group of [melee, ranged]) {
    const divergent = group.filter((s) => s.skillValue !== group[0]?.skillValue);
    if (divergent.length) {
      warnings.push({
        field: `atkProfiles.${name}`,
        message: `Weapons in this profile had different skill values (${group
          .map((s) => s.skillValue)
          .join(', ')}); the roster resolves one per character, so ${group[0].skillValue} was kept.`,
      });
    }
  }

  char.skills.dodge.trained = true;
  char.skills.parry.trained = filled.some((s) => s.weaponType === 'melee');
  char.weapons = loadoutFromSlots(name, filled).weapons;
  return char;
}

// ────────────────────────────────────────────────────────────
// The migration
// ────────────────────────────────────────────────────────────

/**
 * Convert a whole v1 session into a v2 roster.
 *
 * Combatant ids REUSE the legacy tabId. That is free correctness:
 * defence/{tabId} documents, TurnParticipant.tabId and narrative
 * targetName linkage all keep resolving with no rewriting.
 */
export function migrateSessionV1toV2(input: LegacySession): MigrationResult {
  const acc: Migration = { warnings: [], combatants: [], loadouts: [], byCharacterId: new Map(), actorId: null };
  combatantsFromTabs(acc, input.tabs ?? []);

  // ---- Attack profiles become loadouts (and sometimes combatants) ----
  const bindings = input.atkProfiles?._charBindings ?? {};
  const activeProfile = input.atkProfiles?.current ?? null;
  for (const [name, slots] of Object.entries(input.atkProfiles?.list ?? {})) {
    migrateProfile(acc, { name, slots: slots ?? [], binding: bindings[name], active: name === activeProfile });
  }

  const { combatants, loadouts, actorId, warnings } = acc;
  const characters = templateCharacters(input.tgtTemplates ?? {});
  const targetId =
    input.activeTab && combatants.some((c) => c.id === input.activeTab) ? input.activeTab : (combatants[0]?.id ?? null);

  return { combatants, loadouts, characters, actorId, targetId, warnings };
}

/** What the migration builds up as it walks the legacy session. */
interface Migration {
  warnings: ValidationWarning[];
  combatants: Combatant[];
  loadouts: Loadout[];
  /** Character.id → combatantId, so a profile bound to a tab's character reuses it. */
  byCharacterId: Map<string, string>;
  actorId: string | null;
}

/** Tabs become combatants; a tab with no target is skipped, with a warning. */
function combatantsFromTabs(acc: Migration, tabs: LegacyTab[]): void {
  tabs.forEach((tab, index) => {
    if (!tab?.target) {
      acc.warnings.push({ field: `tabs[${index}]`, message: 'Tab had no target and was skipped.' });
      return;
    }
    const combatant = combatantFromLegacyTab(tab as Tab, index);
    acc.combatants.push(combatant);
    if (combatant.kind === 'character') acc.byCharacterId.set(combatant.sheet.id, combatant.id);
  });
}

interface LegacyProfile {
  name: string;
  slots: Slot[];
  binding: { character?: Character } | undefined;
  /** Was this the profile selected when the session was saved? */
  active: boolean;
}

/** One attack profile: always a loadout, sometimes a combatant, and the actor when it was active. */
function migrateProfile(acc: Migration, profile: LegacyProfile): void {
  const loadout = loadoutFromSlots(profile.name, profile.slots);
  acc.loadouts.push(loadout);
  const bound = profile.binding?.character ? normalizeCharacter(profile.binding.character) : null;
  const combatantId = bound
    ? migrateBoundProfile(acc, bound, profile.slots, loadout)
    : migrateLoose(acc, profile, loadout);
  if (profile.active && combatantId !== null) acc.actorId = combatantId;
}

/**
 * A profile bound to a character: that character, carrying the loadout.
 *
 * Already on the roster as a target? Reuse it - same person.
 *
 * A profile's slots are normally derived FROM the character's weapons,
 * so the sheet already has them and is the better source (it keeps
 * weapon ids and `righteousFury: null` inheritance). But a profile can
 * be hand-built and then bound to a character - the demo scenarios arm
 * a 2-weapon Marine from a 4-weapon profile. When the profile carries
 * MORE than the sheet, the sheet is stale: the profile is what the
 * player actually had equipped, so adopt it.
 */
function migrateBoundProfile(acc: Migration, bound: Character, slots: Slot[], loadout: Loadout): string {
  const loadoutRef = { id: loadout.id, name: loadout.name };
  const existing = acc.byCharacterId.get(bound.id);
  if (existing) {
    const c = acc.combatants.find((x) => x.id === existing);
    if (c) c.loadoutRef = loadoutRef;
    return existing;
  }
  let combatant = combatantFromCharacter(bound, { order: acc.combatants.length });
  if (loadout.weapons.length > (bound.weapons ?? []).length) combatant = applyLoadoutSlots(combatant, slots);
  combatant.loadoutRef = loadoutRef;
  acc.combatants.push(combatant);
  acc.byCharacterId.set(bound.id, combatant.id);
  return combatant.id;
}

/**
 * Unbound: only the active profile becomes a combatant. The rest stay
 * in the library as loadouts - they were never a person, just a kit.
 */
function migrateLoose(acc: Migration, profile: LegacyProfile, loadout: Loadout): string | null {
  if (!profile.active || loadout.weapons.length === 0) return null;
  const shell = characterFromProfile(profile.name, profile.slots, acc.warnings);
  const combatant = combatantFromCharacter(shell, { kind: 'statblock', order: acc.combatants.length });
  combatant.loadoutRef = { id: loadout.id, name: loadout.name };
  acc.combatants.push(combatant);
  return combatant.id;
}

/** Target templates become library statblocks. */
function templateCharacters(templates: Record<string, Target>): Character[] {
  return Object.entries(templates).map(([name, target]) => {
    const shell = characterFromStatblock(target);
    shell.name = name;
    shell.tags = ['template'];
    return shell;
  });
}
